import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { convertToVwMob } from '../Utils/responsive';
import mainLogo from '../assets/Logo/main_logo.png';
import kakaoLogo from '../assets/Icon/footer/kakao_icon.png';
import callIcon from '../assets/Icon/footer/famicons_call-sharp.svg';
import emailIcon from '../assets/Icon/footer/ic_round-email.svg';

const KAKAO_CHANNEL_CHAT_URL = 'http://pf.kakao.com/_xkHknxd/chat';

type MobileFooterProps = { showBottomNav?: boolean };

const MobileFooter = React.forwardRef<HTMLElement, MobileFooterProps>(({ showBottomNav = false }, ref) => {
  const { i18n, t } = useTranslation('translation');
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const handleKakaoChannelInquiry = () => {
    window.location.href = KAKAO_CHANNEL_CHAT_URL;
  };

  return (
    <Wrapper ref={ref} $showBottomNav={showBottomNav}>
      <Inner>
        <Logo src={mainLogo} alt="Mission Fund" />

        <Details>
          <div>미션펀드 | 대표자: 김햇님</div>
          <div>Korea: (32720) 충남 금산군 제원면 웨일즈마을</div>
          <ContactGroup>
            <ContactLine>
              <ContactIcon src={callIcon} alt="" aria-hidden="true" />
              <span>1899-0693</span>
            </ContactLine>
            <ContactLine>
              <ContactIcon src={emailIcon} alt="" aria-hidden="true" />
              <span>cs@missionfund.org</span>
            </ContactLine>
          </ContactGroup>
          <div>United States: 45824 Boulder Way, Temecula CA 92592</div>
        </Details>

        <Section>
          <SectionTitle>서비스</SectionTitle>
          <SectionLink>모금 프로젝트 생성</SectionLink>
          <SectionLink>모금 지원상담 신청</SectionLink>
          <SectionLink>후원참여</SectionLink>
        </Section>

        <Section>
          <SectionTitle>개인정보∙이용약관</SectionTitle>
          <SectionLink>개인정보 처리방침</SectionLink>
          <SectionLink>서비스 약관 및 운영 정책</SectionLink>
        </Section>

        <KakaoButton type="button" onClick={handleKakaoChannelInquiry}>
          <KakaoDot src={kakaoLogo} alt="Kakao" />
          카카오톡 채널 문의
        </KakaoButton>

        <Divider />

        <Statement>
          미션펀드는 그리스도의 몸 전체가 사명을 완수하도록 돕습니다.
          <br />
          MissionFund helps the Body of Christ fulfill its mission.
        </Statement>

        <LangSelect
          aria-label="language"
          value={currentLang}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
        >
          <option value="ko">{t('translation:lang.ko')}</option>
          <option value="en">{t('translation:lang.en')}</option>
        </LangSelect>
      </Inner>
    </Wrapper>
  );
});

const BOTTOM_NAV_HEIGHT = 104;

const Wrapper = styled.footer<{ $showBottomNav?: boolean }>`
  background: #EEF3F9;
  padding: ${convertToVwMob(28)} ${convertToVwMob(16)} ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const Inner = styled.div`
  margin: 0 auto;
  max-width: ${convertToVwMob(520)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  align-items: center;
  text-align: center;
`;

const Logo = styled.img`
  height: ${convertToVwMob(38)};
  object-fit: contain;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  font-size: ${convertToVwMob(12)}; /* Caption/Regular12pt */
  line-height: ${convertToVwMob(16)}; /* 12 + 4 */
  color: ${({ theme }) => theme.colors.grayScale[70]};

  & > * + * {
    margin-top: ${convertToVwMob(4)};
  }
`;

const ContactGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ContactLine = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(6)};
  width: 100%;
`;

const ContactIcon = styled.img`
  width: ${convertToVwMob(12)};
  height: ${convertToVwMob(12)};
  flex: 0 0 ${convertToVwMob(12)};
  display: inline-block;
  object-fit: contain;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(10)};
  align-items: center;
  margin-bottom: ${convertToVwMob(30)};
`;

const SectionTitle = styled.div`
  font-weight: 700; /* Caption/Bold16pt */
  font-size: ${convertToVwMob(16)};
  line-height: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[70]}; /* GreyScale/70 */
  margin-top: ${convertToVwMob(8)};
`;

const SectionLink = styled.div`
  font-weight: 500; /* Caption/Medium16pt */
  font-size: ${convertToVwMob(16)};
  line-height: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[60]}; /* GreyScale/60 */
`;

const Statement = styled.div`
  font-size: ${convertToVwMob(12)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
  line-height: ${convertToVwMob(16)};
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.grayScale[30]};
  margin: ${convertToVwMob(8)} 0 ${convertToVwMob(8)};
`;

const LangSelect = styled.select`
  height: ${convertToVwMob(34)};
  min-width: ${convertToVwMob(96)};
  padding: 0 ${convertToVwMob(34)} 0 ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(6)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[40]};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.grayScale[80]};
  font-size: ${convertToVwMob(12)};
  line-height: ${convertToVwMob(34)};
  cursor: pointer;

  /* match design: custom caret */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23929292' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${convertToVwMob(12)} center;
  background-size: ${convertToVwMob(16)} ${convertToVwMob(16)};
`;

const KakaoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(10)};
  width: ${convertToVwMob(186)};
  height: ${convertToVwMob(36)};
  padding: 0 ${convertToVwMob(14)};
  border-radius: ${convertToVwMob(18)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[60]};
  background: transparent; /* Fill 없음: 푸터 배경색 노출 */
  color: ${({ theme }) => theme.colors.grayScale[80]};
  font-size: ${convertToVwMob(13)};
  font-weight: 700;
  font-family: Pretendard;
  cursor: pointer;
  margin-bottom: ${convertToVwMob(20)};
`;

const KakaoDot = styled.img`
  width: ${convertToVwMob(18)};
  height: ${convertToVwMob(18)};
`;

export default MobileFooter;


